import { useState, useContext } from "react";
import { Mail, Lock, User, Eye, EyeOff, Phone, LoaderCircle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import api from "../api/axios";

export default function RegisterPage() {
  const { login } = useContext(AuthContext);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setError("");
    setMessage("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setIsSubmitting(true);
    try {
      const { data } = await api.post("/auth/register", {
        fullName,
        email,
        phone,
        password,
      });

      login(
        {
          _id: data._id,
          fullName: data.fullName,
          email: data.email,
          phone: data.phone,
        },
        data.token
      );
      navigate("/dashboard");
    } catch (err) {
      const data = err.response?.data;
      if (data?.details?.length) {
        setError(data.details.join(" · "));
      } else {
        setError(data?.message || "Registration failed");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-[calc(100vh-72px)] flex items-center justify-center bg-slate-950 px-4 py-10">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(37,99,235,0.35),_transparent_35%),radial-gradient(circle_at_bottom_right,_rgba(250,204,21,0.18),_transparent_30%)]" />

      <div className="relative w-full max-w-md rounded-[2rem] border border-white/15 bg-slate-900/75 p-8 text-white shadow-2xl backdrop-blur-xl">
        <p className="mx-auto mb-4 inline-flex rounded-full border border-cyan-300/35 bg-cyan-300/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-cyan-200">
          New account setup
        </p>
        <h2 className="mb-3 text-center text-3xl font-bold text-white">Open your MekPay account</h2>
        <p className="mb-6 text-center text-sm leading-6 text-slate-300">
          Set up your profile in under 2 minutes to start funding your wallet, sending money, paying bills, and managing crypto transactions.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-200">Full name</label>
            <div className="flex items-center rounded-xl border border-white/15 bg-white/5 px-3 py-2 focus-within:ring-2 focus-within:ring-cyan-300/35">
              <User className="mr-2 text-slate-300" size={20} />
              <input
                type="text"
                placeholder="Enter your full name"
                className="w-full bg-transparent text-white placeholder:text-slate-400 outline-none"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-200">Email address</label>
            <div className="flex items-center rounded-xl border border-white/15 bg-white/5 px-3 py-2 focus-within:ring-2 focus-within:ring-cyan-300/35">
              <Mail className="mr-2 text-slate-300" size={20} />
              <input
                type="email"
                placeholder="you@example.com"
                className="w-full bg-transparent text-white placeholder:text-slate-400 outline-none"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-200">Phone number</label>
            <div className="flex items-center rounded-xl border border-white/15 bg-white/5 px-3 py-2 focus-within:ring-2 focus-within:ring-cyan-300/35">
              <Phone className="mr-2 text-slate-300" size={20} />
              <input
                type="tel"
                placeholder="+2348012345678"
                className="w-full bg-transparent text-white placeholder:text-slate-400 outline-none"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-200">Password</label>
            <div className="flex items-center rounded-xl border border-white/15 bg-white/5 px-3 py-2 focus-within:ring-2 focus-within:ring-cyan-300/35">
              <Lock className="mr-2 text-slate-300" size={20} />
              <input
                type={showPassword ? "text" : "password"}
                placeholder="********"
                className="w-full bg-transparent text-white placeholder:text-slate-400 outline-none"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="ml-2 text-slate-300 hover:text-white"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            <p className="mt-2 text-xs text-slate-400">At least 8 characters with uppercase, lowercase, and a number — e.g. <span className="text-slate-300">MekPay123</span></p>
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-200">Confirm password</label>
            <div className="flex items-center rounded-xl border border-white/15 bg-white/5 px-3 py-2 focus-within:ring-2 focus-within:ring-cyan-300/35">
              <Lock className="mr-2 text-slate-300" size={20} />
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="********"
                className="w-full bg-transparent text-white placeholder:text-slate-400 outline-none"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="ml-2 text-slate-300 hover:text-white"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            aria-busy={isSubmitting}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-accent py-3 font-semibold text-slate-950 transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isSubmitting && <LoaderCircle size={18} className="animate-spin" aria-hidden="true" />}
            {isSubmitting ? "Creating account…" : "Create secure account"}
          </button>

          <p className="text-center text-xs text-slate-300">
            By creating an account, you agree to our <Link to="/terms" className="font-semibold text-cyan-300">Terms</Link> and <Link to="/privacy" className="font-semibold text-cyan-300">Privacy Policy</Link>.
          </p>

          {message && <p className="mt-3 rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-200">{message}</p>}
          {error && <p className="mt-3 rounded-xl border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">{error}</p>}
        </form>

        <p className="mt-6 text-center text-sm text-slate-300">
          Already have an account?{" "}
          <Link to="/login" className="font-semibold text-cyan-300">
            Sign in here
          </Link>
        </p>
      </div>
    </div>
  );
}
